import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  Dialog, DialogType, Checkbox, ActionButton,
  PrimaryButton, DefaultButton, Label,
  getTheme, AnimationClassNames,
} from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SmartRichTextEditor } from '@bingads-webui-campaign-react/smart-component-text';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { mobileDevice } from '@bingads-webui-campaign-react/mca-core';
import { useMobileSupportEnabled, useInstrumentationServiceContext } from '@bingads-webui-campaign-react/smart-page-context';
import { useContextLocale } from '@bingads-webui-campaign-react/smart-page-data-service';
import { useElementViewed } from '@bingads-webui-campaign-react/smart-component-util';

import { PrivacyContent } from './privacy-content';
import { privacyContentMaxLength } from '../data-config';
import { getPrivacyBoilerplate } from './privacy-content-configs';

const { semanticColors, fonts } = getTheme();

const getStyles = (_, { palette = {} }) => ({
  actionGroup: {
    textAlign: 'right',
    margin: '30px 0 5px',

    '& > button:not(:last-child)': {
      marginRight: '10px',
    },
  },
  rte: {
    '& .smart-rich-text-editor-label': {
      fontWeight: 400,
      lineHeight: '20px',
      marginBottom: 20,
    },
    '& .rich-text-editor-wrapper': {
      overflow: 'auto',
      minHeight: 300,
      maxHeight: 400,
    },
  },
  previewButton: {
    color: palette.themePrimary || '#0078d4',
    fontWeight: 600,
    textDecoration: 'underline',
    paddingLeft: 0,
    marginBottom: 5,

    '& .ms-Button-label': {
      margin: 0,
    },
  },
  reviewCheckboxContainer: {
    marginBottom: 25,
  },
  topBackButton: {
    position: 'absolute',
    left: 63,
    [mobileDevice]: {
      left: 10,
      top: 10,
    },
  },
  previewTitle: {
    [mobileDevice]: {
      paddingLeft: 40,
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export const labelErrorStyle = {
  root: [
    fonts.small,
    AnimationClassNames.slideDownIn20,
    {
      color: semanticColors.errorText,
      margin: 0,
      paddingTop: 5,
      display: 'flex',
      alignItems: 'center',
    },
  ],
};

const dialogContentStyle = {
  title: {
    fontSize: 24,
    padding: 20,
    lineHeight: '28px',
    textAlign: 'center',
    [mobileDevice]: {
      fontSize: 20,
    },
  },
  inner: {
    margin: '5px 50px 15px',
    [mobileDevice]: {
      margin: 0,
    },
  },
};

const elementScope = 'EditPrivacyDialog';

export const UnstyledEditPrivacyDialog = ({
  defaultValue,
  onSave,
  onClose,
  i18n,
  classes,
  // Mainly use to disable the check box
  isPrivacyStatementReviewed,
  businessName,
}) => {
  const locale = useContextLocale();
  const [value, setValue] = useState(defaultValue || getPrivacyBoilerplate(i18n, locale));
  // Internal state for check box
  const [isReviewedInternal, setIsReviewedInternal] = useState(isPrivacyStatementReviewed);
  const [isPreview, setIsPreview] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [showReviewError, setShowReviewError] = useState(null);

  const logger = useInstrumentationServiceContext();
  const isMobile = useMobileSupportEnabled();
  useElementViewed(logger, elementScope);

  const onChange = useCallback(({ value: newValue }) => {
    setValue(newValue);
  }, []);

  const onHandleSave = useCallback(() => {
    logger.logTrace(`${elementScope}|SaveButton|Clicked`);

    if (isReviewedInternal && !validationError) {
      onSave(value);
      onClose();
    } else {
      setShowReviewError(true);
    }
  }, [isReviewedInternal, onClose, onSave, validationError, value, logger]);

  const onHandleCancel = useCallback(() => {
    logger.logTrace(`${elementScope}|CancelButton|Clicked`);
    onClose();
  }, [logger, onClose]);

  const onToggleReview = useCallback((_, checked) => {
    setIsReviewedInternal(checked);
    logger.logTrace(`${elementScope}|ReviewToggle|Changed|${checked}`);
  }, [logger]);

  const onClickBack = useCallback(() => {
    setIsPreview(false);
    logger.logTrace(`${elementScope}|BackButton|Clicked`);
  }, [logger]);

  const onClickPreview = useCallback(() => {
    setIsPreview(true);
    logger.logTrace(`${elementScope}|PreviewButton|Clicked`);
  }, [logger]);

  const dialogTitle = useMemo(() => {
    if (isPreview) {
      return (
        <div className={classes.dialogTitle}>
          <ActionButton
            className={classNames(classes.topBackButton, 'back-to-edit-privacy-policy-button')}
            onClick={onClickBack}
            iconProps={{ iconName: 'Back' }}
            text={isMobile ? '' : i18n.getString(_TL_('Back to editor'))}
          />
          <span className={classes.previewTitle}>{i18n.getString(_TL_('Preview your privacy policy'))}</span>
        </div>
      );
    }

    return i18n.getString(_TL_('Edit your privacy policy'));
  }, [classes, i18n, isPreview, isMobile, onClickBack]);

  return (
    <Dialog
      hidden={false}
      onDismiss={onClose}
      minWidth="360px"
      maxWidth="1030px"
      dialogContentProps={{
        type: DialogType.close,
        title: dialogTitle,
        styles: dialogContentStyle,
      }}
      modalProps={{
        isBlocking: true,
        className: 'edit-privacy-dialog',
      }}
    >
      {
        isPreview &&
        <PrivacyContent i18n={i18n} value={value} businessName={businessName} />
      }
      {
        !isPreview &&
        <React.Fragment>
          <SmartRichTextEditor
            className={classNames('privacy-policy-rich-text-editor', classes.rte)}
            label={(
              <ReactTemplate
                model={{ bold: text => <b>{text}</b> }}
                template={i18n.getString(_TL_('Create a privacy statement describing how you\'ll use your customers\' data. This statement is legally required. We\'ll provide you with a generic privacy statement that may apply in part to your business; however, it is {{bold}}your responsibility to ensure that the statement is fully accurate.{{/bold}} Please note we\'ll display Microsoft\'s privacy policy alongside the one you create. This is because Microsoft processes your customers\' data before sharing it with you.'))}
              />
            )}
            onChange={onChange}
            placeholder={i18n.getString(_TL_('You can write your privacy policy here.'))}
            maxCharCount={privacyContentMaxLength}
            value={value}
            isRequired
            onValidate={errMsg => setValidationError(errMsg)}
          />
          <div>
            <ActionButton
              className={classNames(classes.previewButton, 'preview-privacy-policy-button')}
              onClick={onClickPreview}
              disabled={!!validationError}
              text={i18n.getString(_TL_('Preview my privacy policy'))}
            />
          </div>
        </React.Fragment>
      }
      <div className={classes.reviewCheckboxContainer}>
        <Checkbox
          className="review-privacy-policy-checkbox"
          label={i18n.getString(_TL_('I have reviewed and/or edited my Smart Page\'s privacy policy and confirm it outlines how my business processes and uses customer data.'))}
          checked={isReviewedInternal}
          disabled={isPrivacyStatementReviewed}
          onChange={onToggleReview}
        />
        {
          !isReviewedInternal && showReviewError
          && (
            <Label styles={labelErrorStyle} className="review-policy-error-msg">
              {i18n.getString(_TL_('Please confirm you have reviewed your privacy policy'))}
            </Label>
          )
        }
      </div>
      <div className={classes.actionGroup}>
        <DefaultButton
          className="cancel-edit-policy-button"
          onClick={onHandleCancel}
          text={i18n.getString(_TL_('Cancel'))}
        />
        <PrimaryButton
          className="save-policy-button"
          onClick={onHandleSave}
          text={i18n.getString(_TL_('Save'))}
        />
      </div>
    </Dialog>
  );
};

UnstyledEditPrivacyDialog.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  defaultValue: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  classes: PropTypes.shape({}).isRequired,
  isPrivacyStatementReviewed: PropTypes.bool,
};

UnstyledEditPrivacyDialog.defaultProps = {
  isPrivacyStatementReviewed: false,
};

export const EditPrivacyDialog = withDefaultStyles(
  UnstyledEditPrivacyDialog,
  getStyles
);

